/**
 * Expand cart weight — TEMPORARY, for testing.
 *
 * Adds the weight of every line to the cart page and the cart drawer, plus the
 * cart's total weight under the totals.
 *
 * All figures come from `/cart.js`, so nothing is worked out here that Shopify
 * does not already know: `item.grams` is the weight of a single unit, and
 * `cart.total_weight` is the whole cart. Weights are shown in grams and
 * kilograms regardless of the store's weight unit, which is one of the reasons
 * this is test code rather than a shipped feature.
 *
 * Remove the feature by deleting this file, `snippets/ex___cart-weight.liquid`,
 * and the `{% render 'ex___cart-weight' %}` line in `layout/theme.liquid`.
 * See `docs/cart-weight-testing.md`.
 */

import { StandardEvents } from '@shopify/events';

const LINE_CLASS = 'ex-cart-weight';
const TOTAL_CLASS = 'ex-cart-weight__total';

/**
 * Formats grams the way a person reads a weight — kilograms once it is worth it.
 * @param {number} grams - The weight in grams.
 * @returns {string} The formatted weight.
 */
const formatWeight = (grams) => (grams >= 1000 ? `${(grams / 1000).toFixed(2)} kg` : `${Math.round(grams)} g`);

/**
 * Reads the current cart.
 * @returns {Promise<object|null>} The cart, or null if it could not be read.
 */
const fetchCart = async () => {
  try {
    const response = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });

    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
};

/**
 * Writes a line of text into the element, creating it on first use. Unchanged
 * text is left alone so repeated renders don't churn the DOM.
 * @param {Element} parent - The element to write into.
 * @param {string} className - The class of the element that holds the text.
 * @param {string} text - The text to write.
 */
const write = (parent, className, text) => {
  let element = parent.querySelector(`.${className}`);

  if (!element) {
    element = document.createElement('div');
    element.className = className;
    parent.append(element);
  }

  if (element.textContent !== text) element.textContent = text;
};

/**
 * Paints every weight the cart knows about.
 * @param {object|null} cart - The cart, as returned by `/cart.js`.
 */
const render = (cart) => {
  if (!cart) return;

  // The cart page and the drawer both render one row per line, keyed the same
  // way, so a single pass covers both.
  for (const item of cart.items) {
    for (const row of document.querySelectorAll(`[data-key="${CSS.escape(item.key)}"]`)) {
      const target = row.querySelector('.cart-items__details') ?? row;

      write(target, LINE_CLASS, `${formatWeight(item.grams)} each · ${formatWeight(item.grams * item.quantity)} total`);
    }
  }

  for (const container of document.querySelectorAll('.cart-totals__container')) {
    write(container, TOTAL_CLASS, `Total weight: ${formatWeight(cart.total_weight)}`);
  }
};

/**
 * Repaints after a quantity change or a removal.
 *
 * The event fires before the cart's own request finishes, and the cart then
 * re-renders its section — which wipes anything injected into it. So wait for
 * that request, then paint on the next frame and once more after the section
 * has settled.
 * @param {CustomEvent} event - The cart lines update event.
 */
const handleCartUpdate = async (event) => {
  await event.detail?.promise?.catch(() => {});

  const cart = await fetchCart();

  render(cart);
  requestAnimationFrame(() => render(cart));
  setTimeout(() => render(cart), 300);
};

document.addEventListener(StandardEvents.cartLinesUpdate, handleCartUpdate);

render(await fetchCart());
